"use client";

import * as SwitchPrimitives from "@radix-ui/react-switch";
import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface ToggleSwitchProps extends React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> {
  size?: "sm" | "md" | "lg";
  color?: "green" | "amber" | "blue" | "purple";
}

const colorMap = {
  green: {
    on: "bg-gradient-to-r from-[#10B981] to-[#22C55E]",
    ring: "focus-visible:ring-[#10B981]/20",
  },
  amber: {
    on: "bg-gradient-to-r from-[#D97706] to-[#F59E0B]",
    ring: "focus-visible:ring-[#D97706]/20",
  },
  blue: {
    on: "bg-gradient-to-r from-[#2563EB] to-[#3B82F6]",
    ring: "focus-visible:ring-[#2563EB]/20",
  },
  purple: {
    on: "bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6]",
    ring: "focus-visible:ring-[#7C3AED]/20",
  },
};

const SIZE = {
  sm: { rootW: 44, rootH: 24, thumb: 18, checkedOffset: 22 },
  md: { rootW: 52, rootH: 30, thumb: 24, checkedOffset: 26 },
  lg: { rootW: 56, rootH: 32, thumb: 26, checkedOffset: 28 },
};

function ToggleSwitchInner(
  {
    className,
    size = "md",
    color = "green",
    disabled,
    checked: controlledChecked,
    defaultChecked,
    onCheckedChange,
    ...props
  }: ToggleSwitchProps,
  ref: React.Ref<React.ElementRef<typeof SwitchPrimitives.Root>>,
) {
  const isControlled = controlledChecked !== undefined;
  const [internalChecked, setInternalChecked] = React.useState(defaultChecked ?? false);
  const checked = isControlled ? controlledChecked : internalChecked;
  const colors = colorMap[color];
  const dims = SIZE[size];

  const handleChange = (v: boolean) => {
    if (!isControlled) setInternalChecked(v);
    onCheckedChange?.(v);
  };

  return (
    <SwitchPrimitives.Root
      ref={ref}
      checked={checked}
      onCheckedChange={handleChange}
      disabled={disabled}
      className={cn(
        "peer relative inline-flex shrink-0 cursor-pointer items-center rounded-full",
        "transition-all duration-300 ease-in-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        colors.ring,
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked
          ? cn(colors.on, "shadow-sm")
          : "bg-[#E5E7EB] shadow-inner",
        className,
      )}
      style={{
        width: dims.rootW,
        height: dims.rootH,
        WebkitTapHighlightColor: "transparent",
      }}
      {...props}
    >
      <SwitchPrimitives.Thumb asChild>
        <motion.span
          layout
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 30,
            mass: 1,
          }}
          className={cn(
            "pointer-events-none block rounded-full bg-white",
            "ring-0 relative flex items-center justify-center",
            disabled && "shadow-none",
          )}
          style={{
            width: dims.thumb,
            height: dims.thumb,
          }}
          animate={{
            x: checked ? dims.checkedOffset : 0,
            boxShadow: checked
              ? "0 2px 6px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.08)"
              : "0 1px 3px rgba(0,0,0,0.12), 0 1px 1px rgba(0,0,0,0.06)",
          }}
        >
          <AnimatePresence mode="wait">
            {checked ? (
              <motion.svg
                key="check"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute text-gray-700"
                style={{ width: dims.thumb * 0.5, height: dims.thumb * 0.5 }}
              >
                <polyline points="20 6 9 17 4 12" />
              </motion.svg>
            ) : (
              <motion.svg
                key="x"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute text-gray-400"
                style={{ width: dims.thumb * 0.5, height: dims.thumb * 0.5 }}
              >
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </motion.svg>
            )}
          </AnimatePresence>
        </motion.span>
      </SwitchPrimitives.Thumb>
    </SwitchPrimitives.Root>
  );
}

const ToggleSwitch = React.forwardRef(ToggleSwitchInner);
ToggleSwitch.displayName = "ToggleSwitch";

export { ToggleSwitch };
